/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  memo,
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from 'react';
import { cloneDeep } from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  ELEMENT_QUICK_ACTIONS,
  STATEMENT_ELEMENT_ID_PREFIX,
  STATEMENT_LEFT_ELEMENT_ID_PREFIX,
  SIDE_BY_SIDE_VIEW_RIGHT_BLOCK,
  getElementHightlightState,
  getQuickActionByElement,
  NO_TICKMARK_OPTION,
  NO_WORKPAPER_OPTION,
  ELLIPSIS_OPTION,
  LOADER,
} from 'constants/feature/statement-content-constants';
import { ELEMENT_SELECT_MODES } from 'constants/feature/modes-constants';
import {
  getElementFlaggedIcon,
  trialBalanceWPIcon,
  VERIFIED_STATUS,
  WORKPAPER_TYPE,
} from 'constants/feature/element-panel-constants';
import {
  ELEMENT_BLOCK,
  COMFORT_ASSIGN_ELEMENT,
  SIDE_BY_SIDE_VIEW_SELECTED_ELEMENT,
  TIEOUT_ELEMENT_LEFT,
  ANNOTATION_ICON_SIZE,
  CROSS_ICON_SIZE,
} from 'constants/feature/tieout-element-constants';
import { FEATURE_NAMES } from '../../../constants/feature-flag-constants';
import {
  AUTO_TIEOUT_WORKPAPER_TYPE,
  AUTO_TIEOUT_TB_WORKPAPER_TYPE,
} from 'constants/feature/statement-creation-constants';
import {
  handleHighlightMappedElements,
  removeObjectsByOrdinal,
} from 'constants/feature/tieout-element-utils';
import { shouldShowContextMenu as calculateContextMenuVisibility } from 'utils/auto-tieout-element-utils';
import { getTickmarksForRevisionRequest } from 'api/tickmark-api';
import { getWorkpapersForRevisionRequest } from 'api/workpaper-api';
import { initElementPanel } from 'store/actions/common/element-panel-actions';
import {
  unlinkElementWorkpaperFromAutoTieout,
  attachTickmarkToElement,
  getHistoryForElementPanel,
} from 'store/actions/element-panel-actions';
import {
  addElementFormulaRow,
  setSelectedRowAction,
} from 'store/actions/formula-actions';
import { addElementToInternalReference } from 'store/actions/internal-reference-actions';
import {
  handleSystemFlagOverride,
  clearContentHighlightSelector,
  handleElementQuickAction,
  handleElementFormulaHighlight,
  removeElementFormulaHighlight,
} from 'store/actions/statement-content-actions';
import { clickElementInContentWhileBatch } from 'store/actions/batch-panel-actions';
import {
  addElementForCopyFormula,
  removeElementFromBatchSelection,
} from 'store/actions/copy-formula-actions';
import {
  deselectElementContentPanel,
  clearSideBySideElementsMap,
} from 'store/actions/action-creators';
import { updateSideBySideElementCacheByCallouts } from 'store/actions/statement-content-annotation-creation-actions';
import { updateElementCacheByCalloutAction } from 'store/actions/element-cache-actions';
import { updateWorkpaper } from 'store/actions/workpaper-actions';
import WorkpaperForm from 'models/form/workpaper-form-model';
import { isNullOrUndefined } from 'utils/object-utils';
import {
  getShowCalloutValue,
  getElementHighlightStatus,
  hasAnnotations,
  isElementReportPreviewPage,
} from 'utils/ocr-annotation-utils';
import { createConfirmOverrideSystemFlagHandlers } from 'utils/confirm-override-system-flag-utils';
import FeatureFlagComponent from 'components/common/feature-flag-component';
import Tooltip from 'components/common/tool-tip-component';
import PreviewReportSelectedableElement from './tieout-element-html/_preview-report-selectedable-element-component';
import TieoutElementCallouts from 'components/feature/statement-content-panel/tieout-element-callouts';
import { SelectedableElement as AnnotationDisplay } from 'components/feature/statement-content-panel/tieout-callout-element-component-ocr';
import QuickActionElementContextMenu from './quick-action-element-context-menu';
import CreateTickmark from 'components/feature/statement-content-panel/quick-action/create-tickmarks';
import ConfirmOverrideSystemFlag from 'components/feature/element-panel/confirm-override-system-flag-modal-component';
import CreateWorkPaper from './quick-action/create-workpaper';
import AttachExistingTickmarks from 'components/feature/statement-content-panel/quick-action/attach-existing-tickmarks';
import AttachExistingWorkpapers from 'components/feature/statement-content-panel/quick-action/attach-existing-workpapers';
import useHoverEffect from 'components/hooks/useHoverEffects';
import { ReactComponent as CrossIcon } from 'icons/cross-icon.svg';
import { isModeForAddCursor } from 'utils/modes-utils';
import ManageElementAttributesMenu from './quick-action/manage-element-attributes-menu';
import ConfirmationModal from 'components/common/confirmation-modal';
import { getHistoryForElementRequest } from 'api/element-api';

export const CALLOUT_MAX_WIDTH = '120px';

/* LOCAL THUNKS */
/**
 * Handles what happens when an element is clicked in the content panel
 * different modes will dictate what happens when an element is clicked
 * @param {int} param.elementId
 * @param {int} param.sectionId
 */
export const onElementClick =
  ({ elementId, sectionId, color, isClickedFromPanel, isDisabled }) =>
  async (dispatch, getStore) => {
    const { modes } = getStore().ui.statementPage;
    const { copyFormulaSelectedElement } = getStore().data.copyFormula;
    const { selectMode } = modes;
    switch (selectMode) {
      case ELEMENT_SELECT_MODES.BATCH_WITH_BANNER:
      case ELEMENT_SELECT_MODES.BATCH: {
        dispatch(clickElementInContentWhileBatch({ elementId, sectionId }));
        break;
      }
      case ELEMENT_SELECT_MODES.RANGE: {
        break;
      }
      case ELEMENT_SELECT_MODES.INTERNAL_REFERENCE: {
        dispatch(
          addElementToInternalReference({
            targetElementId: elementId,
            isFromSuggestion: false,
          }),
        );
        break;
      }
      case ELEMENT_SELECT_MODES.FORMULA: {
        await dispatch(
          addElementFormulaRow({
            elementId,
            sectionId,
          }),
        );
        //if an element for a formula is already selected, set the selected row as null
        dispatch(setSelectedRowAction(null));
        break;
      }
      case ELEMENT_SELECT_MODES.COPY_FORMULA: {
        if (!isDisabled) {
          if (
            copyFormulaSelectedElement.selectedElementIds.some(
              // eslint-disable-next-line eqeqeq
              (eleId) => eleId == elementId,
            )
          ) {
            dispatch(removeElementFromBatchSelection({ elementId }));
            dispatch(deselectElementContentPanel({ elementId }));
          } else {
            dispatch(
              addElementForCopyFormula({
                elementId,
              }),
            );
          }
        }
        break;
      }

      default: {
        if (!isClickedFromPanel) {
          await dispatch(clearContentHighlightSelector());
        }
        await dispatch(initElementPanel({ elementId, color }));
      }
    }
  };

/* COMPONENT */
export const TieoutElement = ({
  id,
  sectionId,
  name,
  children,
  isSideBySideViewDisabled,
  elementDataparts,
  elementData,
  handleElementClick,
  handleAnnotationClick,
  isCursorAlias,
  isElementSelectedForSideBySideMap,
  getCountForSelectedElementForSideBySideMapping,
  displayElementDetailsOnStatementPage,
  leftSideView,
  numberOfSourceElementSelected,
  numberOfTargetElementSelected,
  elementPanelElementId,
  annotationDisplayFilter,
  isCftEnabled,
  annotationPosition,
  rightPanelState,
  highlightedElementId,
  isElementSelected,
  elementHighlightState,
  isPartOfTableMatrix,
  isSideBySideViewMode,
  isSideBySideView,
  autoTieoutElementData,
  isAutoTieoutMode,
  handleAutoTieoutElementClick,
  handleTrialBalanceElementClick,
  workFlowStatus,
  workpaperDetails,
  isReadOnlyStatement,
  setMenuOptions,
  menuOptions,
  selectMode,
  zoomRatio,
  viewMode,
  isTrialBalanceMode,
  tbWorkFlowStatus,
  trialBalanceMappedElements,
  isStatementWrappedOrPendingApproval,
  isSnapshotReadOnly,
  showSelectionPointerForMappedElements,
  showCrossIconByStmtWrappedUpState,
  highlightMappedElements,
  batchElementList,
  editPermissionAutotieout,
}) => {
  let elementId = useMemo(
    () => id.substring(STATEMENT_ELEMENT_ID_PREFIX.length),
    [id],
  );
  const customElementId = useMemo(
    () => elementId && !!elementId.split('_').length && elementId.split('_')[0],
    [elementId],
  );
  const hideAnnotations = useMemo(
    () => ({
      flag: false,
      annotations: false,
      elementsSelectedCount: false,
      firstDataPart: false,
      middleDataPart: false,
      lastDataPart: false,
    }),
    [],
  );

  const TIEOUT_ELEMENT_CLASS = useMemo(
    () => (leftSideView ? TIEOUT_ELEMENT_LEFT : ELEMENT_BLOCK),
    [leftSideView],
  );

  const isHighlighted = useMemo(
    () => highlightedElementId === elementId,
    [highlightedElementId, elementId],
  );

  const { position } = annotationPosition;

  if (elementId && elementId.indexOf('_') >= 0) {
    const elementIdWithDatapart = elementId;
    elementId = elementId.substr(0, elementId.indexOf('_'));
    if (elementDataparts.elements && elementId in elementDataparts.elements) {
      const datapartId = elementIdWithDatapart.substr(
        elementIdWithDatapart.indexOf('_') + 1,
        elementIdWithDatapart.length,
      );

      if (!isNullOrUndefined(datapartId)) {
        if (parseInt(datapartId) !== 0) {
          hideAnnotations.flag = true;
        }
        if (parseInt(datapartId) === 0) {
          hideAnnotations.firstDataPart = true;
        } else if (
          parseInt(datapartId) > 0 &&
          parseInt(datapartId) < elementDataparts.elements[elementId]
        ) {
          hideAnnotations.middleDataPart = true;
        } else {
          hideAnnotations.lastDataPart = true;
        }
        if (datapartId !== elementDataparts.elements[elementId]) {
          hideAnnotations.elementsSelectedCount = true;
        }

        switch (position) {
          case 'left':
          case 'top':
            if (parseInt(datapartId) !== 0) {
              hideAnnotations.annotations = true;
            }
            break;
          case 'right':
          case 'bottom':
            if (datapartId !== elementDataparts.elements[elementId]) {
              hideAnnotations.annotations = true;
            }
            break;
          default:
            break;
        }
      }
    }
  }

  const handleClick = useCallback(
    (event) => {
      if (event) {
        event.stopPropagation();
      }
      handleElementClick({
        elementId,
        sectionId,
        isDisabled: !isPartOfTableMatrix,
      });
    },
    [elementId, sectionId],
  );

  const handleOnAnnotationClick = useCallback(
    (showCallout) => {
      handleAnnotationClick({
        elementId,
        showCallout,
      });
    },
    [elementId],
  );

  return isElementReportPreviewPage(window.location.pathname) ? (
    <PreviewReportSelectedableElement
      elementDetails={elementData}
      id={id}
      elementId={elementId}
      sectionId={sectionId}
      children={children}
      leftSideView={leftSideView}
    />
  ) : (
    <MemoizedSelectedableComponent
      selectableKey={id}
      id={id}
      name={name}
      isHighlighted={isHighlighted}
      onClick={handleClick}
      onAnnotationClick={handleOnAnnotationClick}
      isCursorAlias={isCursorAlias}
      elementData={elementData}
      selected={isElementSelected}
      elementHighlightState={elementHighlightState}
      elementId={elementId}
      sectionId={sectionId}
      panel={rightPanelState}
      elementPanelElementId={elementPanelElementId}
      hideAnnotations={hideAnnotations}
      isDisabled={!isPartOfTableMatrix}
      isSideBySideViewDisabled={isSideBySideViewDisabled(elementId)}
      isCftEnabled={isCftEnabled}
      annotationDisplayFilter={annotationDisplayFilter}
      annotationPosition={annotationPosition}
      isElementSelectedForSideBySideMap={isElementSelectedForSideBySideMap}
      leftSideView={leftSideView}
      getCountForSelectedElementForSideBySideMapping={
        getCountForSelectedElementForSideBySideMapping
      }
      isElementSelected={isElementSelected}
      displayElementDetailsOnStatementPage={
        displayElementDetailsOnStatementPage
      }
      customElementId={customElementId}
      TIEOUT_ELEMENT_CLASS={TIEOUT_ELEMENT_CLASS}
      numberOfSourceElementSelected={numberOfSourceElementSelected}
      numberOfTargetElementSelected={numberOfTargetElementSelected}
      isSideBySideViewMode={isSideBySideViewMode}
      isSideBySideView={isSideBySideView}
      isAutoTieoutMode={isAutoTieoutMode}
      autoTieoutElementData={autoTieoutElementData}
      handleAutoTieoutElementClick={handleAutoTieoutElementClick}
      handleTrialBalanceElementClick={handleTrialBalanceElementClick}
      workFlowStatus={workFlowStatus}
      autoTieoutWPDetails={workpaperDetails.autoTieoutWPDetails}
      isReadOnlyStatement={isReadOnlyStatement}
      setMenuOptions={setMenuOptions}
      menuOptions={menuOptions}
      selectMode={selectMode}
      zoomRatio={zoomRatio}
      viewMode={viewMode}
      isTrialBalanceMode={isTrialBalanceMode}
      tbWorkFlowStatus={tbWorkFlowStatus}
      trialBalanceMappedElements={trialBalanceMappedElements}
      isStatementWrappedOrPendingApproval={isStatementWrappedOrPendingApproval}
      isSnapshotReadOnly={isSnapshotReadOnly}
      showSelectionPointerForMappedElements={
        showSelectionPointerForMappedElements
      }
      showCrossIconByStmtWrappedUpState={showCrossIconByStmtWrappedUpState}
      highlightMappedElements={highlightMappedElements}
      batchElementList={batchElementList}
      editPermissionAutotieout={editPermissionAutotieout}
    >
      {children}
    </MemoizedSelectedableComponent>
  );
};

const _getSelectedModifierClass = ({
  selected,
  panel,
  elementPanelElementId,
  elementId,
  elementHighlightState,
  TIEOUT_ELEMENT_CLASS,
}) =>
  selected
    ? `${TIEOUT_ELEMENT_CLASS}--selected--${getElementHightlightState(
        panel,
        elementPanelElementId,
        elementId,
      )} ${TIEOUT_ELEMENT_CLASS}--selected--${elementHighlightState}`
    : null;

const SelectedableElement = ({
  id,
  name,
  selected,
  elementHighlightState,
  onClick,
  elementData,
  elementId,
  sectionId,
  children,
  panel,
  elementPanelElementId,
  isHighlighted,
  hideAnnotations,
  href,
  isDisabled,
  isSideBySideViewDisabled,
  annotationDisplayFilter,
  annotationPosition,
  isElementSelectedForSideBySideMap,
  getCountForSelectedElementForSideBySideMapping,
  isCursorAlias,
  isCftEnabled,
  isElementSelected,
  isSideBySideView,
  displayElementDetailsOnStatementPage,
  onAnnotationClick,
  leftSideView,
  customElementId,
  TIEOUT_ELEMENT_CLASS,
  numberOfSourceElementSelected,
  numberOfTargetElementSelected,
  isSideBySideViewMode,
  isAutoTieoutMode,
  autoTieoutElementData,
  handleAutoTieoutElementClick,
  handleTrialBalanceElementClick,
  workFlowStatus,
  autoTieoutWPDetails,
  isReadOnlyStatement,
  setMenuOptions,
  menuOptions,
  selectMode,
  zoomRatio,
  viewMode,
  isTrialBalanceMode,
  tbWorkFlowStatus,
  trialBalanceMappedElements,
  isStatementWrappedOrPendingApproval,
  isSnapshotReadOnly,
  showSelectionPointerForMappedElements,
  showCrossIconByStmtWrappedUpState,
  highlightMappedElements,
  batchElementList,
  editPermissionAutotieout,
}) => {
  const [isShowCallout, setShowCalloutVal] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [showTickmarkModal, setShowTickmarkModal] = useState(false);
  const [showWorkPaperModal, setShowWorkPaperModal] = useState(false);
  const [showConfirmOverrideSystemFlag, setShowConfirmOverrideSystemFlag] =
    useState(false);
  const [overrideSystemFlagElement, setOverrideSystemFlagElement] =
    useState(null);
  const [showTickmarkListModal, setShowTickmarkListModal] = useState(false);
  const [existingTickmarkList, setExistingTickmarkList] = useState([]);
  const [showWorkpaperListModal, setShowWorkpaperListModal] = useState(false);
  const [showReviewConfirmationModal, setShowReviewConfirmationModal] =
    useState(false);
  const [existingWorkpaperList, setExistingWorkpaperList] = useState([]);
  const [attachedWorkpaperIds, setAttachedWorkpaperIds] = useState([]);
  const [showManageAttributesMenu, setShowManageAttributesMenu] =
    useState(false);
  const [clickPosition, setClickPosition] = useState(null);

  const dispatch = useDispatch();
  const revision = useSelector((state) => state.data.revision);
  const elementHistory = useSelector(
    (state) => state.data.elementPanel.elementHistory,
  );
  const currentUser = useSelector((state) => state.data.currentUser);
  const elementRef = useRef(null);
  const contextMenuIdRef = useRef(null);
  const menuIdRef = useRef(`menu-${elementId}`);
  const contextMenuPositionRef = useRef(null);
  const { isReviewed, isVerified, isUnverified } = getElementHighlightStatus({
    elementDetails: elementData,
    isElementSelected,
  });

  const _isElementSelectedForSideBySideMap =
    isElementSelectedForSideBySideMap(elementId);
  const _countForSelectedElementForSideBySideMapping =
    getCountForSelectedElementForSideBySideMapping(elementId);

  const shouldHighlightMappedElements = useMemo(() => {
    return handleHighlightMappedElements({
      isSideBySideView,
      highlightMappedElements,
      isAutoTieoutMode,
      isTrialBalanceMode,
      isLeftView: leftSideView,
      elementDetails: elementData,
      autoTieoutMappedElementDetails: isAutoTieoutMode
        ? autoTieoutElementData
        : trialBalanceMappedElements,
    });
  }, [
    isSideBySideView,
    highlightMappedElements,
    isAutoTieoutMode,
    isTrialBalanceMode,
    leftSideView,
    elementId,
    elementData,
    autoTieoutElementData,
    trialBalanceMappedElements,
  ]);

  const getElementStatusColor = useCallback(() => {
    if (shouldHighlightMappedElements)
      return `${TIEOUT_ELEMENT_CLASS}--mapped-element-highlight`;
    if (isNullOrUndefined(_countForSelectedElementForSideBySideMapping)) {
      return isReviewed
        ? `${TIEOUT_ELEMENT_CLASS}--reviewed`
        : isVerified
          ? `${TIEOUT_ELEMENT_CLASS}--verified`
          : isUnverified && `${TIEOUT_ELEMENT_CLASS}--unverified`;
    }
  }, [
    shouldHighlightMappedElements,
    isReviewed,
    isVerified,
    isUnverified,
    _countForSelectedElementForSideBySideMapping,
    TIEOUT_ELEMENT_CLASS,
  ]);

  const handleIndicatorOnHover = useCallback(
    (elementId, showCallout) => {
      const { marker = false, callout = false } = elementData || {};
      if (!annotationDisplayFilter.showAll) {
        isShowCallout &&
          (marker || callout) &&
          dispatch(
            updateSideBySideElementCacheByCallouts({
              elementIds: [elementId],
              showCallout: showCallout,
              isLeftSideView: leftSideView,
            }),
          );
      }
      if (showCallout && !isSideBySideView) {
        dispatch(handleElementFormulaHighlight(elementData, elementRef));
      } else if (!showCallout && !isSideBySideView) {
        dispatch(removeElementFormulaHighlight(elementData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      annotationDisplayFilter.showAll,
      isShowCallout,
      leftSideView,
      annotationPosition.position,
      TIEOUT_ELEMENT_CLASS,
    ],
  );

  const { handleMouseEnter, handleMouseLeave } = useHoverEffect(
    `${TIEOUT_ELEMENT_CLASS}`,
    customElementId,
  );

  useEffect(() => {
    const handleCloseOtherContextMenus = (event) => {
      if (event.detail.id !== contextMenuIdRef.current) {
        setContextMenu(null);
      }
    };

    const handleCloseAllAttributeMenus = (event) => {
      // Close this menu if the event is from a different element or context menu
      if (event.detail.id !== menuIdRef.current) {
        setShowManageAttributesMenu(false);
        setClickPosition(null);
      }
    };

    window.addEventListener(
      'closeOtherContextMenus',
      handleCloseOtherContextMenus,
    );
    window.addEventListener(
      'closeAllAttributeMenus',
      handleCloseAllAttributeMenus,
    );

    return () => {
      window.removeEventListener(
        'closeOtherContextMenus',
        handleCloseOtherContextMenus,
      );
      window.removeEventListener(
        'closeAllAttributeMenus',
        handleCloseAllAttributeMenus,
      );
    };
  }, []);

  // Close ManageElementAttributesMenu when context menu opens
  useEffect(() => {
    if (contextMenu) {
      setShowManageAttributesMenu(false);
      setClickPosition(null);
    }
  }, [contextMenu]);

  const createNewTickmarkHandler = useCallback(() => {
    setShowTickmarkModal(true);
  }, []);

  const createNewWorkPaperHandler = useCallback(() => {
    setShowWorkPaperModal(true);
  }, []);

  const closingAttributeActionWindow = useCallback(() => {
    // Close all attribute menu across all elements
    window.dispatchEvent(
      new CustomEvent('closeAllAttributeMenus', {
        detail: { id: contextMenuIdRef.current },
      }),
    );
    setShowManageAttributesMenu(false);
    setClickPosition(null);
  }, []);

  const onManageAttributesHandler = useCallback((event) => {
    // Close all other attribute menu before opening this one
    window.dispatchEvent(
      new CustomEvent('closeAllAttributeMenus', {
        detail: { id: menuIdRef.current },
      }),
    );

    // Capture position from the actual menu item click event
    if (event?.clientX !== undefined && event?.clientY !== undefined) {
      setClickPosition({ x: event.clientX, y: event.clientY });
    } else if (contextMenuPositionRef.current) {
      // Fallback to context menu position if event is not available
      setClickPosition(contextMenuPositionRef.current);
    }
    setShowManageAttributesMenu(true);
  }, []);

  const getTickmarkHandlers = () => {
    return createConfirmOverrideSystemFlagHandlers(
      overrideSystemFlagElement,
      dispatch,
    );
  };

  const handleUpdateOverrideSystemFlag = async ({ systemFlag }) => {
    if (!overrideSystemFlagElement) {
      throw new Error(
        'Cannot update override system flag: no element selected',
      );
    }

    try {
      dispatch(
        handleSystemFlagOverride({
          element: overrideSystemFlagElement,
          systemFlag,
          revisionId: revision.id,
        }),
      );
    } finally {
      setShowConfirmOverrideSystemFlag(false);
      setOverrideSystemFlagElement(null);
    }
  };

  const createOverrideSystemFlagHandler = (element) => {
    if (element.isSystemOverrideFlagged()) {
      dispatch(
        handleElementQuickAction({
          element,
          action: { type: ELEMENT_QUICK_ACTIONS.OVERRIDE_FLAG },
        }),
      );
    } else {
      setOverrideSystemFlagElement(element);
      setShowConfirmOverrideSystemFlag(true);
    }
    setContextMenu(null);
  };

  const updateTickmarkSubMenu = (tickmarkMenuList, submenuList) => {
    const tickmarkMenuListClone = cloneDeep(tickmarkMenuList);
    tickmarkMenuListClone.forEach((menuItem) => {
      if (menuItem.type === ELEMENT_QUICK_ACTIONS.ADD_TICKMARK) {
        const subMenu = menuItem.children.map((subMenuItem) => {
          if (
            subMenuItem.type === ELEMENT_QUICK_ACTIONS.ATTACH_EXISTING_TICKMARK
          ) {
            return {
              ...subMenuItem,
              children: [...submenuList],
            };
          }
          return subMenuItem;
        });
        menuItem.children = subMenu;
      }
    });
    return tickmarkMenuListClone;
  };

  const updateWorkpaperSubMenu = (workpaperMenuList, submenuList) => {
    const workpaperMenuListClone = cloneDeep(workpaperMenuList);
    workpaperMenuListClone.forEach((menuItem) => {
      if (menuItem.type === ELEMENT_QUICK_ACTIONS.ADD_WORKPAPER) {
        const subMenu = menuItem.children.map((subMenuItem) => {
          if (
            subMenuItem.type === ELEMENT_QUICK_ACTIONS.ATTACH_EXISTING_WORKPAPER
          ) {
            return {
              ...subMenuItem,
              children: [...submenuList],
            };
          }
          return subMenuItem;
        });
        menuItem.children = subMenu;
      }
    });
    return workpaperMenuListClone;
  };

  const onAttachExistingTickmarks = async (element) => {
    setMenuOptions((state) => updateTickmarkSubMenu(state, [LOADER]));
    const response = await getTickmarksForRevisionRequest({
      revisionId: element.revisionId,
    });
    const tickmarkList = removeObjectsByOrdinal(
      response?.data?.result || [],
      element.tickmarks,
    );

    setExistingTickmarkList(tickmarkList);
    if (tickmarkList.length === 0) {
      setMenuOptions((state) =>
        updateTickmarkSubMenu(state, [NO_TICKMARK_OPTION]),
      );
      return;
    }
    const sortedTickmarks = tickmarkList.sort(
      (a, b) => b.elementCount - a.elementCount,
    );
    let tickmarksMenuList = [];

    if (sortedTickmarks.length > 3) {
      const top3Tickmarks = sortedTickmarks.slice(0, 3);
      tickmarksMenuList = top3Tickmarks.map((tickmark) => ({
        ...tickmark,
        id: `${tickmark.tickmarkId}-${tickmark.noteOrdinal}`,
        type: ELEMENT_QUICK_ACTIONS.TICKMARK_LIST_ITEM,
        label: `{${tickmark.noteOrdinal}}...`,
        disabled: false,
        shouldCloseMenuOnClick: true,
        shouldDispatch: false,
        isNotInternationalized: true,
        tooltipConfig: {
          id: `${tickmark.tickmarkId}-${tickmark.noteOrdinal}`,
          text: tickmark.text,
          isNotInternationalized: true,
        },
      }));
      tickmarksMenuList.push(ELLIPSIS_OPTION);
    } else {
      tickmarksMenuList = sortedTickmarks.map((tickmark) => ({
        ...tickmark,
        id: `${tickmark.tickmarkId}-${tickmark.noteOrdinal}`,
        type: ELEMENT_QUICK_ACTIONS.TICKMARK_LIST_ITEM,
        label: `{${tickmark.noteOrdinal}}...`,
        disabled: false,
        shouldCloseMenuOnClick: true,
        shouldDispatch: false,
        isNotInternationalized: true,
        tooltipConfig: {
          id: `${tickmark.tickmarkId}-${tickmark.noteOrdinal}`,
          text: tickmark.text,
          isNotInternationalized: true,
        },
      }));
    }
    setMenuOptions((state) => updateTickmarkSubMenu(state, tickmarksMenuList));
  };

  const onAttachExistingWorkpapers = async (element) => {
    try {
      setMenuOptions((state) => updateWorkpaperSubMenu(state, [LOADER]));

      const allWorkpapersResponse = await getWorkpapersForRevisionRequest({
        revisionId: element.revisionId,
      });

      const allWorkpapers = allWorkpapersResponse?.data?.result || [];
      const elementWorkpaperRefs = element.workpaperReferenceNumberList || [];
      const attachedReferenceNumbers = new Set(
        elementWorkpaperRefs.map((ref) => ref.split('|')[0]),
      );
      const availableWorkpapers = allWorkpapers.filter(
        (wp) =>
          !attachedReferenceNumbers.has(wp.referenceNumber) &&
          wp.workpaperType !== AUTO_TIEOUT_WORKPAPER_TYPE &&
          wp.workpaperType !== AUTO_TIEOUT_TB_WORKPAPER_TYPE &&
          wp.workpaperType !== WORKPAPER_TYPE.OMNIA_TRIAL_BALANCE,
      );

      setExistingWorkpaperList(availableWorkpapers);
      setAttachedWorkpaperIds([]);

      if (availableWorkpapers.length === 0) {
        setMenuOptions((state) =>
          updateWorkpaperSubMenu(state, [NO_WORKPAPER_OPTION]),
        );
        return;
      }

      const sortedWorkpapers = availableWorkpapers.sort(
        (a, b) => b.elementCount - a.elementCount,
      );
      let workpapersMenuList = [];

      if (sortedWorkpapers.length > 3) {
        const top3Workpapers = sortedWorkpapers.slice(0, 3);
        workpapersMenuList = top3Workpapers.map((workpaper) => {
          const hasReferenceName =
            workpaper.referenceName && workpaper.referenceName.trim() !== '';

          return {
            ...workpaper,
            id: `${workpaper.workpaperRefId}`,
            type: ELEMENT_QUICK_ACTIONS.WORKPAPER_LIST_ITEM,
            label: hasReferenceName
              ? `${workpaper.referenceNumber}...`
              : workpaper.referenceNumber,
            shouldCloseMenuOnClick: true,
            shouldDispatch: false,
            isNotInternationalized: true,
            tooltipConfig: hasReferenceName
              ? {
                  id: `workpaper-ref-name-${workpaper.workpaperRefId}`,
                  text: workpaper.referenceName,
                  isNotInternationalized: true,
                }
              : null,
          };
        });
        workpapersMenuList.push({
          ...ELLIPSIS_OPTION,
          parentType: ELEMENT_QUICK_ACTIONS.ADD_WORKPAPER,
        });
      } else {
        workpapersMenuList = sortedWorkpapers.map((workpaper) => {
          const hasReferenceName =
            workpaper.referenceName && workpaper.referenceName.trim() !== '';

          return {
            ...workpaper,
            id: `${workpaper.workpaperRefId}`,
            type: ELEMENT_QUICK_ACTIONS.WORKPAPER_LIST_ITEM,
            label: hasReferenceName
              ? `${workpaper.referenceNumber}...`
              : workpaper.referenceNumber,
            shouldCloseMenuOnClick: true,
            shouldDispatch: false,
            isNotInternationalized: true,
            tooltipConfig: hasReferenceName
              ? {
                  id: `workpaper-ref-name-${workpaper.workpaperRefId}`,
                  text: workpaper.referenceName,
                  isNotInternationalized: true,
                }
              : null,
          };
        });

        // No ellipsis needed when 3 or fewer workpapers
      }
      setMenuOptions((state) =>
        updateWorkpaperSubMenu(state, workpapersMenuList),
      );
    } catch (error) {
      console.error('Error fetching workpapers:', error);
      setMenuOptions((state) =>
        updateWorkpaperSubMenu(state, [NO_WORKPAPER_OPTION]),
      );
    }
  };

  const onOpenExistingTickmarksModal = () => {
    setShowTickmarkListModal(true);
  };

  const onOpenExistingWorkpapersModal = () => {
    setShowWorkpaperListModal(true);
  };

  const isElementVerifiedByCurrentUser = useCallback(() => {
    const history = elementHistory?.entries?.find(
      (entry) =>
        entry.elementAction === VERIFIED_STATUS &&
        entry.changedBy === currentUser.id,
    );
    return !!history;
  }, [elementHistory?.entries, currentUser.id]);

  const onConfirmReviewClick = useCallback(() => {
    dispatch(
      handleElementQuickAction({
        element: elementData,
        action: { type: ELEMENT_QUICK_ACTIONS.REVIEW },
      }),
    );
    setShowReviewConfirmationModal(false);
  }, [elementData]);

  const onOpenReviewConfirmationModal = useCallback(() => {
    if (!elementData.isReviewed() && isElementVerifiedByCurrentUser()) {
      setShowReviewConfirmationModal(true);
    } else {
      onConfirmReviewClick();
    }
  }, [elementData, isElementVerifiedByCurrentUser, onConfirmReviewClick]);

  const onTickmarkListItemClick = (element, action) => {
    dispatch(
      attachTickmarkToElement({ tickmark: action, elementDetails: element }),
    );
  };

  const onWorkpaperListItemClick = async (element, action) => {
    try {
      const workpaperModel = new WorkpaperForm({
        data: {
          revisionId: action.revisionId,
          elementId: element.id,
          referenceNumber: action.referenceNumber,
          referenceName: action.referenceName,
          workpaperRefId: action.workpaperRefId,
          workpaperRefCreatedBy: action.workpaperRefCreatedBy,
          workpaperRefCreatedDate: action.workpaperRefCreatedDate,
          creationType: action.creationType,
        },
      });

      dispatch(updateWorkpaper({ workpaper: workpaperModel }));
      dispatch(
        updateElementCacheByCalloutAction({
          elementIds: [element.id],
          showCallout: true,
        }),
      );
    } catch (error) {
      console.error('Error attaching workpaper:', error);
    }
  };

  const handleEllipsisOptionClick = (element, action) => {
    const parentType = action.parentType;
    if (parentType === ELEMENT_QUICK_ACTIONS.ADD_WORKPAPER) {
      onOpenExistingWorkpapersModal(element);
    } else {
      onOpenExistingTickmarksModal(element);
    }
  };

  const handleQuickActionMenuClick = useCallback(
    ({ element, action, event }) => {
      const actionToHandlerMap = {
        [ELEMENT_QUICK_ACTIONS.CREATE_NEW_TICKMARK]: createNewTickmarkHandler,
        [ELEMENT_QUICK_ACTIONS.OVERRIDE_FLAG]: () =>
          createOverrideSystemFlagHandler(element),
        [ELEMENT_QUICK_ACTIONS.CREATE_NEW_WORKPAPER]: createNewWorkPaperHandler,
        [ELEMENT_QUICK_ACTIONS.ATTACH_EXISTING_TICKMARK]:
          onAttachExistingTickmarks,
        [ELEMENT_QUICK_ACTIONS.ATTACH_EXISTING_WORKPAPER]:
          onAttachExistingWorkpapers,
        [ELEMENT_QUICK_ACTIONS.ELLIPSIS_OPTION]: handleEllipsisOptionClick,
        [ELEMENT_QUICK_ACTIONS.TICKMARK_LIST_ITEM]: onTickmarkListItemClick,
        [ELEMENT_QUICK_ACTIONS.WORKPAPER_LIST_ITEM]: onWorkpaperListItemClick,
        [ELEMENT_QUICK_ACTIONS.MANAGE_ATTRIBUTES]: () =>
          onManageAttributesHandler(event),
        [ELEMENT_QUICK_ACTIONS.REVIEW]: onOpenReviewConfirmationModal,
      };

      actionToHandlerMap[action.type]?.(element, action);
    },
    [
      createNewTickmarkHandler,
      createOverrideSystemFlagHandler,
      createNewWorkPaperHandler,
      onAttachExistingTickmarks,
      onAttachExistingWorkpapers,
      handleEllipsisOptionClick,
      onTickmarkListItemClick,
      onWorkpaperListItemClick,
      onManageAttributesHandler,
    ],
  );

  const handleContextMenu = useCallback(
    (event, elementData) => {
      closingAttributeActionWindow();
      const shouldShowContextMenu = calculateContextMenuVisibility({
        selectMode,
        workFlowStatus,
        isSideBySideView,
        isAutoTieoutMode,
        isTrialBalanceMode,
        isLeftView: leftSideView,
        batchElementList,
        elementId: elementData?.id,
      });

      if (
        !shouldShowContextMenu ||
        selectMode === ELEMENT_SELECT_MODES.FORMULA
      ) {
        return;
      }

      let mappedElements = {};
      if (isAutoTieoutMode) mappedElements = autoTieoutElementData;
      else if (isTrialBalanceMode) mappedElements = trialBalanceMappedElements;

      const menuOptionsByElement = getQuickActionByElement(
        elementData,
        isStatementWrappedOrPendingApproval || isSnapshotReadOnly,
        mappedElements,
        isCftEnabled,
      );
      if (
        isReadOnlyStatement ||
        (!menuOptionsByElement &&
          Array.isArray(menuOptionsByElement) &&
          menuOptionsByElement.length === 0)
      ) {
        return;
      }

      if (elementData.isVerified()) {
        dispatch(getHistoryForElementPanel(elementData.id));
      }

      setMenuOptions(menuOptionsByElement);

      event.preventDefault();
      event.stopPropagation();

      // Capture the click position for the manage attributes menu
      contextMenuPositionRef.current = {
        x: event.clientX,
        y: event.clientY,
      };

      // Get the element ID from the DOM
      const clickedElement = event.currentTarget;
      const elementIdFromDom = clickedElement.getAttribute('id');

      // Find the element by ID to ensure we have the correct DOM reference
      const targetElement = document.getElementById(elementIdFromDom);

      if (!targetElement) {
        console.warn('Target element not found:', elementIdFromDom);
        return;
      }

      // Get the bounding rectangle of the actual DOM element
      const targetRect = targetElement.getBoundingClientRect();

      // Generate unique ID for this context menu instance
      const menuId = `context-menu-${Date.now()}-${Math.random()}`;
      contextMenuIdRef.current = menuId;

      // Dispatch event to close other context menus
      window.dispatchEvent(
        new CustomEvent('closeOtherContextMenus', { detail: { id: menuId } }),
      );

      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        elementId: elementIdFromDom,
        targetRect: {
          top: targetRect.top,
          right: targetRect.right,
          bottom: targetRect.bottom,
          left: targetRect.left,
          width: targetRect.width,
          height: targetRect.height,
        },
      });
    },
    [elementData, workFlowStatus, selectMode],
  );

  const closeContextMenu = useCallback(
    (data, item, event) => {
      const { type, shouldCloseMenuOnClick, shouldDispatch } = item || {};

      if (
        type !== ELEMENT_QUICK_ACTIONS.LOADER &&
        !isNullOrUndefined(item) &&
        !isNullOrUndefined(data)
      ) {
        if (shouldDispatch) {
          dispatch(handleElementQuickAction({ element: data, action: item }));
        } else {
          handleQuickActionMenuClick({ element: data, action: item, event });
        }
      }

      if (!item || shouldCloseMenuOnClick) {
        setContextMenu(null);
      }
    },
    [dispatch, handleQuickActionMenuClick],
  );

  const TIEOUT_ELEMENT_ID_PREFIX = leftSideView
    ? `${STATEMENT_LEFT_ELEMENT_ID_PREFIX}${elementId}`
    : id;

  const selectedModifierClass = _getSelectedModifierClass({
    selected,
    panel,
    elementPanelElementId,
    elementId,
    elementHighlightState,
    TIEOUT_ELEMENT_CLASS,
  });

  const sourceElementClicked = autoTieoutElementData?.getSourceElementClicked();
  const targetElementClicked = autoTieoutElementData?.getTargetElementClicked();

  let autoTieoutElementToHighlight = sourceElementClicked
    ? sourceElementClicked
    : targetElementClicked;

  // Trial Balance: Get clicked elements for highlight linking
  const tbSourceElementClicked =
    trialBalanceMappedElements?.getSourceElementClicked?.();
  const tbTargetElementClicked =
    trialBalanceMappedElements?.getTargetElementClicked?.();

  // Determine which element to highlight for Trial Balance
  const trialBalanceElementToHighlight = tbSourceElementClicked
    ? tbSourceElementClicked
    : tbTargetElementClicked;

  const isRightViewBatchMode =
    !leftSideView &&
    (isAutoTieoutMode || isTrialBalanceMode) &&
    isModeForAddCursor(selectMode);

  const shouldApplyCursorAlias =
    isCursorAlias &&
    (isRightViewBatchMode ||
      !(isDisabled || showSelectionPointerForMappedElements));

  const sideBySideViewSelectedElementClass = () => {
    if (_isElementSelectedForSideBySideMap) {
      if (
        hideAnnotations.firstDataPart ||
        hideAnnotations.middleDataPart ||
        hideAnnotations.lastDataPart
      ) {
        let className = 'selected-element-for-side-by-side-view--partial';

        if (hideAnnotations.firstDataPart) {
          className += ' selected-element-for-side-by-side-view--first';
        } else if (hideAnnotations.lastDataPart) {
          className += ' selected-element-for-side-by-side-view--last';
        }

        return className;
      } else {
        return `${SIDE_BY_SIDE_VIEW_SELECTED_ELEMENT}`;
      }
    }
  };

  const renderSelectedableElement = () => {
    const isAutoTieoutUnlinkIconVisible =
      !leftSideView &&
      ((isAutoTieoutMode &&
        workFlowStatus.isCompleted &&
        autoTieoutElementToHighlight?.[
          leftSideView ? 'sourceElementId' : 'targetElementId'
        ] == elementId) ||
        (isTrialBalanceMode &&
          tbWorkFlowStatus?.isCompleted &&
          trialBalanceElementToHighlight?.elementId == elementId));
    const shouldShowAnnotationsByMode =
      leftSideView && (isAutoTieoutMode || isTrialBalanceMode);
    return (
      <>
        <a
          ref={elementRef}
          className={classnames({
            [`${TIEOUT_ELEMENT_CLASS}--disabled`]:
              (isDisabled || showSelectionPointerForMappedElements) &&
              !isRightViewBatchMode,
            [`${TIEOUT_ELEMENT_CLASS}--cursor-disabled`]:
              isSideBySideViewDisabled,
            [`${TIEOUT_ELEMENT_CLASS}`]: true,
            [`${TIEOUT_ELEMENT_CLASS}-${customElementId}`]: true,
            [`${TIEOUT_ELEMENT_CLASS}--selected`]: selected,
            [`${selectedModifierClass}`]: true,
            [`${TIEOUT_ELEMENT_CLASS}--highlighted`]: isHighlighted,
            [`${TIEOUT_ELEMENT_CLASS}--cursor-alias`]: shouldApplyCursorAlias,
            [`${TIEOUT_ELEMENT_CLASS}--highlight-auto-tieout-element`]:
              autoTieoutElementToHighlight?.[
                leftSideView ? 'sourceElementId' : 'targetElementId'
              ] == elementId ||
              (isTrialBalanceMode &&
                trialBalanceElementToHighlight?.elementId == elementId),
            [sideBySideViewSelectedElementClass()]:
              !!sideBySideViewSelectedElementClass(), // for both mode side/auto
          })}
          id={TIEOUT_ELEMENT_ID_PREFIX}
          disabled={isDisabled}
          name={name}
          onClick={() => {
            if (
              isAutoTieoutMode &&
              workFlowStatus.isCompleted &&
              !isModeForAddCursor(selectMode)
            ) {
              handleAutoTieoutElementClick({
                elementId,
              });
            }
            if (
              isTrialBalanceMode &&
              tbWorkFlowStatus?.isCompleted &&
              !isModeForAddCursor(selectMode)
            ) {
              handleTrialBalanceElementClick({
                elementId,
              });
            }
            onClick();
          }}
          onContextMenu={(e) =>
            editPermissionAutotieout
              ? handleContextMenu(e, elementData)
              : undefined
          }
          onMouseEnter={(e) => {
            e.stopPropagation();
            handleIndicatorOnHover(elementId, true);
            handleMouseEnter();
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            if (!annotationDisplayFilter.expandAllElements || !selected) {
              handleIndicatorOnHover(elementId, false);
            }
            handleMouseLeave();
          }}
          data-element-id={elementId}
          data-section-id={sectionId}
          href={href}
        >
          {displayElementDetailsOnStatementPage &&
          !hideAnnotations.flag &&
          annotationDisplayFilter.flag
            ? getElementFlaggedIcon({
                elementDetails: elementData,
                iconSize: ANNOTATION_ICON_SIZE,
                BLOCK: `${TIEOUT_ELEMENT_CLASS}--flag`,
              })
            : null}
          <FeatureFlagComponent
            name={FEATURE_NAMES.COMFORT_ASSIGNED_FEATURE}
            fallback={
              <span
                className={classnames(
                  TIEOUT_ELEMENT_CLASS,
                  getElementStatusColor(),
                  {
                    [`${TIEOUT_ELEMENT_CLASS}--disabled`]:
                      (isDisabled || showSelectionPointerForMappedElements) &&
                      !isRightViewBatchMode,
                    [`${TIEOUT_ELEMENT_CLASS}--cursor-disabled`]:
                      isSideBySideViewDisabled,
                    [`${TIEOUT_ELEMENT_CLASS}--cursor-alias`]:
                      shouldApplyCursorAlias,
                  },
                )}
              >
                {children}
              </span>
            }
          >
            <span
              className={classnames(
                TIEOUT_ELEMENT_CLASS,
                `${TIEOUT_ELEMENT_CLASS}-${customElementId}`,
                getElementStatusColor(),
                {
                  [`${TIEOUT_ELEMENT_CLASS}--disabled`]:
                    (isDisabled || showSelectionPointerForMappedElements) &&
                    !isRightViewBatchMode,
                  [`${TIEOUT_ELEMENT_CLASS}--selectable-element-cursor`]:
                    _isElementSelectedForSideBySideMap && isSideBySideViewMode,
                  [`${COMFORT_ASSIGN_ELEMENT}`]:
                    elementData && elementData.isComfortAssign(),
                  [`${TIEOUT_ELEMENT_CLASS}--cursor-disabled`]:
                    isSideBySideViewDisabled,
                  [`${TIEOUT_ELEMENT_CLASS}--cursor-alias`]:
                    shouldApplyCursorAlias,
                },
              )}
              onMouseEnter={(e) => {
                handleMouseEnter();
              }}
              onMouseLeave={(e) => {
                handleMouseLeave();
              }}
            >
              {children}
            </span>
          </FeatureFlagComponent>
          {displayElementDetailsOnStatementPage &&
            !isNullOrUndefined(_countForSelectedElementForSideBySideMapping) &&
            !hideAnnotations.elementsSelectedCount && (
              <span className={`${SIDE_BY_SIDE_VIEW_SELECTED_ELEMENT}__count`}>
                {_countForSelectedElementForSideBySideMapping}
              </span>
            )}
          {isAutoTieoutUnlinkIconVisible &&
            showCrossIconByStmtWrappedUpState && (
              <Tooltip
                id={`${TIEOUT_ELEMENT_CLASS}__detach-wp-tooltip`}
                text={
                  isTrialBalanceMode
                    ? 'trial-balance-view.auto-tieout.wp-remove-reference.tooltip'
                    : 'common.unlink'
                }
              >
                <CrossIcon
                  width={CROSS_ICON_SIZE}
                  height={CROSS_ICON_SIZE}
                  className={`${TIEOUT_ELEMENT_CLASS}__cross-icon`}
                  onClick={() => {
                    dispatch(
                      unlinkElementWorkpaperFromAutoTieout({
                        workpaper: autoTieoutWPDetails,
                        elementDetails: elementData,
                      }),
                    );
                    dispatch(clearSideBySideElementsMap());
                  }}
                />
              </Tooltip>
            )}
        </a>

        {displayElementDetailsOnStatementPage &&
          !hideAnnotations.annotations &&
          elementData &&
          hasAnnotations(elementData, isCftEnabled) && (
            <TieoutElementCallouts
              elementRef={elementRef}
              annotationPosition={annotationPosition}
              leftSideView={leftSideView}
              getShowCalloutValue={getShowCalloutValue}
              shouldShowAnnotationsByMode={shouldShowAnnotationsByMode}
              isShowCallout={isShowCallout}
              setShowCalloutVal={setShowCalloutVal}
              elementData={elementData}
              annotationDisplayFilter={annotationDisplayFilter}
              id={TIEOUT_ELEMENT_ID_PREFIX}
              ELEMENT_BLOCK={TIEOUT_ELEMENT_CLASS}
              displayElementDetailsOnStatementPage={
                displayElementDetailsOnStatementPage
              }
              sectionId={sectionId}
              onAnnotationClick={onAnnotationClick}
              numberOfSourceElementSelected={numberOfSourceElementSelected}
              numberOfTargetElementSelected={numberOfTargetElementSelected}
            >
              <AnnotationDisplay
                hideAnnotations={hideAnnotations}
                annotationDisplayFilter={annotationDisplayFilter}
                elementDetails={elementData}
                isOCR={false}
                shouldShowAnnotationsByMode={shouldShowAnnotationsByMode}
              />
            </TieoutElementCallouts>
          )}
        {trialBalanceWPIcon({
          viewMode,
          elementData,
          BLOCK: TIEOUT_ELEMENT_CLASS,
        })}
        {contextMenu && (
          <QuickActionElementContextMenu
            data={elementData}
            menuOptions={menuOptions}
            position={contextMenu}
            onClose={closeContextMenu}
            isOCR={false}
            zoomRatio={zoomRatio}
            containerId={
              !leftSideView && isSideBySideView
                ? SIDE_BY_SIDE_VIEW_RIGHT_BLOCK
                : undefined
            }
          />
        )}
        <CreateTickmark
          show={showTickmarkModal}
          setShow={setShowTickmarkModal}
          elementDetails={elementData}
        />
        {showConfirmOverrideSystemFlag && overrideSystemFlagElement && (
          <ConfirmOverrideSystemFlag
            onClickCancel={() => {
              setShowConfirmOverrideSystemFlag(false);
              setOverrideSystemFlagElement(null);
            }}
            updateOverrideSystemFlag={handleUpdateOverrideSystemFlag}
            {...getTickmarkHandlers()}
            disabled={false}
          />
        )}
        {showWorkPaperModal && (
          <CreateWorkPaper
            show={showWorkPaperModal}
            setShow={setShowWorkPaperModal}
            elementDetails={elementData}
          />
        )}
        {showManageAttributesMenu && (
          <ManageElementAttributesMenu
            show={showManageAttributesMenu}
            setShow={setShowManageAttributesMenu}
            elementDetails={elementData}
            clickPosition={clickPosition}
          />
        )}
        {showTickmarkListModal && (
          <AttachExistingTickmarks
            onClose={() => setShowTickmarkListModal(false)}
            listData={existingTickmarkList}
            elementIds={[parseInt(elementRef?.current?.dataset?.elementId)]}
            revisionId={revision.id}
          />
        )}
        {showWorkpaperListModal && (
          <AttachExistingWorkpapers
            onClose={() => setShowWorkpaperListModal(false)}
            listData={existingWorkpaperList}
            elementDetails={elementData}
            revisionId={revision.id}
            alreadyAddedWorkpaperIds={attachedWorkpaperIds}
          />
        )}
        {showReviewConfirmationModal && (
          <ConfirmationModal
            title={'element-panel.checkbox.review.confirmation.modal.title'}
            textId={'element-panel.checkbox.review.confirmation.modal.text'}
            onClose={() => setShowReviewConfirmationModal(false)}
            onConfirm={onConfirmReviewClick}
            primaryButtonText={'common.yes'}
            secondaryButtonText={'common.no'}
          />
        )}
      </>
    );
  };

  return renderSelectedableElement();
};

const MemoizedSelectedableComponent = React.memo(SelectedableElement);

TieoutElement.propTypes = {
  /** unique id for this element (should be CFTO_ELEMENT_XXXXXXX) */
  id: PropTypes.string.isRequired,
  /** name attribute for element, NOTE: probably can be filtered/deleted if we decide we don't need it */
  name: PropTypes.string,
  /** Link attribute */
  href: PropTypes.string,
  /** this function is used mediator to have handle API call triggering in side by side view */
  handleElementClick: PropTypes.func,
  /** object of statmentContent.elementDataParts or leftStatmentContent.leftElementDataParts will be used based on the left or right statements in SideBySide View */
  elementDataparts: PropTypes.object,
  /** object of statmentContent.elementCache or leftStatmentContent.leftElementCache will be used based on the left or right statements in SideBySide View */
  elementData: PropTypes.func,
  /** boolean value used to handle the pointer for left statement in SideBySide View */
  isCursorAlias: PropTypes.bool,
  /**A function which tells us, if an element is part of side by side view mapping */
  isElementSelectedForSideBySideMap: PropTypes.func,
  /**It gives us info about the sequence number of mapped element. For example, an element is nth mapping in the sequence */
  getCountForSelectedElementForSideBySideMapping: PropTypes.func,
  /** A function to help us decide if a particular element in source or target statement (when in side by side view mode) should be disabled or enabled*/
  isSideBySideViewDisabled: PropTypes.func,
};

export default memo(TieoutElement);
